/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React from 'react';
import './CollectionSelector.css';

interface CollectionSelectorProps {
  collections: string[]; // Array of handles
  selectedIndex: number;
  onSelectionChange: (index: number) => void;
  loading?: boolean;
  showProductCount?: boolean;
  getCollectionDisplayName?: (handle: string, index: number) => string; // Changed: now receives handle
  label?: string;
}

const CollectionSelector: React.FC<CollectionSelectorProps> = ({
  collections,
  selectedIndex,
  onSelectionChange,
  loading = false,
  showProductCount = false,
  getCollectionDisplayName,
  label = 'Select Collection:'
}) => {
  const getDisplayName = (handle: string, index: number) => {
    if (getCollectionDisplayName) {
      return getCollectionDisplayName(handle, index);
    }
    // Fallback: format handle nicely
    return handle
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (collections.length === 0) {
    return null;
  }

  // If only one collection, show it as text instead of dropdown
  if (collections.length === 1) {
    return (
      <div className="collection-selector single-collection">
        <div className="collection-name">{getDisplayName(collections[0], 0)}</div>
      </div>
    );
  }

  return (
    <div className="collection-selector">
      <label htmlFor="collection-select" className="collection-selector-label">
        {label}
      </label>
      <select
        id="collection-select"
        value={selectedIndex}
        onChange={(e) => onSelectionChange(parseInt(e.target.value))}
        disabled={loading}
        className="collection-selector-dropdown"
      >
        {collections.map((handle, index) => (
          <option key={index} value={index}>
            {getDisplayName(handle, index)}
          </option>
        ))}
      </select>
    </div>
  );
};

export default CollectionSelector;